import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Plus,
  Globe,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  Check,
  X,
  Trash2,
  Loader2,
  Clock,
  AlertTriangle,
  Settings2,
  Download,
  Search,
  LayoutGrid,
  List as ListIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  checkDomainReputation,
  DOMAIN_DNSBL_ZONES,
  IP_DNSBL_ZONES,
  MAX_QUORUM,
  type QuorumConfig,
} from "@/lib/domain-reputation.functions";
import {
  enqueueDomainChecks,
  getDomainCheckQueueStatus,
  cancelDomainCheckJob,
} from "@/lib/domain-check-queue.functions";

export const Route = createFileRoute("/_authenticated/domains")({
  component: DomainsPage,
  head: () => ({ meta: [{ title: "Domains — InboxPulse" }] }),
});

const QUORUM_KEY = "dnsbl-quorum-config-v1";
const ALL_ZONES = [...DOMAIN_DNSBL_ZONES, ...IP_DNSBL_ZONES] as readonly string[];

function downloadFile(filename: string, content: string, mime: string) {
  try {
    const blob = new Blob([content], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    toast.error(`Export fehlgeschlagen: ${(err as Error).message}`);
  }
}

function csvCell(v: unknown): string {
  if (v == null) return "";
  const s = Array.isArray(v) ? v.join("|") : typeof v === "object" ? JSON.stringify(v) : String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportDnsblAudit(
  domain: string,
  entries: ReadonlyArray<Record<string, unknown>>,
  meta: { checked_at?: string | null; resolvers_blocked?: string[] },
  format: "json" | "csv",
) {
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const safe = domain.replace(/[^a-zA-Z0-9.-]/g, "_");
  if (format === "json") {
    const payload = {
      domain,
      exported_at: new Date().toISOString(),
      checked_at: meta.checked_at ?? null,
      resolvers_blocked: meta.resolvers_blocked ?? [],
      dnsbl: entries,
    };
    downloadFile(
      `dnsbl-audit_${safe}_${ts}.json`,
      JSON.stringify(payload, null, 2),
      "application/json",
    );
    toast.success(`JSON exportiert (${entries.length} Lookups)`);
    return;
  }
  const header = [
    "domain",
    "zone",
    "target",
    "kind",
    "listed",
    "blocked",
    "quorum_required",
    "quorum_hits",
    "quorum_confirmed",
    "resolvers_tried",
    "raw_answers",
    "resolver",
    "error",
    "per_resolver",
  ];
  const rows = entries.map((e) => [
    domain,
    e.zone,
    e.target,
    e.kind,
    e.listed,
    e.blocked,
    e.quorum_required ?? "",
    e.quorum_hits ?? "",
    e.quorum_confirmed ?? "",
    e.resolvers_tried,
    e.raw_answers,
    e.resolver ?? "",
    e.error ?? "",
    e.per_resolver ? JSON.stringify(e.per_resolver) : "",
  ]);
  const csv = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
  downloadFile(`dnsbl-audit_${safe}_${ts}.csv`, csv, "text/csv");
  toast.success(`CSV exportiert (${entries.length} Lookups)`);
}

function loadQuorum(): QuorumConfig {
  try {
    const raw = typeof window !== "undefined" ? localStorage.getItem(QUORUM_KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<QuorumConfig>;
      const def = Math.min(MAX_QUORUM, Math.max(1, Math.round(Number(parsed.default ?? 2))));
      const perZone: Record<string, number> = {};
      for (const [z, v] of Object.entries(parsed.perZone ?? {})) {
        const n = Math.round(Number(v));
        if (Number.isFinite(n)) perZone[z] = Math.min(MAX_QUORUM, Math.max(1, n));
      }
      return { default: def, perZone };
    }
  } catch {
    /* ignore */
  }
  return { default: 2, perZone: {} };
}

function DomainsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [quorum, setQuorum] = useState<QuorumConfig>(() => loadQuorum());
  const [quorumOpen, setQuorumOpen] = useState(false);
  const [filters, setFilters] = useState<
    Set<"listed" | "unconfirmed" | "blocked" | "clean" | "err" | "unchecked">
  >(new Set());
  const [viewMode, setViewMode] = useState<"cards" | "list">(() => {
    if (typeof window === "undefined") return "cards";
    return (localStorage.getItem("domains-view-mode") as "cards" | "list") ?? "cards";
  });
  const [detailId, setDetailId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"score" | "age" | "checked" | "domain">("score");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [minScore, setMinScore] = useState<number>(0);
  const [ageFilter, setAgeFilter] = useState<"all" | "new" | "young" | "established" | "unknown">(
    "all",
  );
  const [checkFilter, setCheckFilter] = useState<"all" | "checked" | "unchecked" | "stale">("all");
  const [pageSize, setPageSize] = useState<number>(() => {
    if (typeof window === "undefined") return 20;
    const v = Number(localStorage.getItem("domains-page-size"));
    return Number.isFinite(v) && v > 0 ? v : 20;
  });
  const [visibleCount, setVisibleCount] = useState<number>(pageSize);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const resetSortFilters = () => {
    setSortBy("score");
    setSortDir("desc");
    setMinScore(0);
    setAgeFilter("all");
    setCheckFilter("all");
  };
  const setView = (m: "cards" | "list") => {
    setViewMode(m);
    try {
      localStorage.setItem("domains-view-mode", m);
    } catch {
      /* ignore */
    }
  };
  const toggleFilter = (k: "listed" | "unconfirmed" | "blocked" | "clean" | "err" | "unchecked") =>
    setFilters((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const saveQuorum = useCallback((q: QuorumConfig) => {
    setQuorum(q);
    try {
      localStorage.setItem(QUORUM_KEY, JSON.stringify(q));
    } catch {
      /* ignore */
    }
  }, []);
  const { data } = useQuery({
    queryKey: ["domains"],
    queryFn: async () =>
      (await supabase.from("domains").select("*").order("created_at", { ascending: false })).data ??
      [],
  });

  const domainIds = (data ?? []).map((d) => d.id);
  const { data: history } = useQuery({
    queryKey: ["domain-history", domainIds],
    enabled: domainIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("domain_reputation_history")
        .select("domain_id, score, checked_at, blacklists, notes, dmarc_policy")
        .in("domain_id", domainIds)
        .order("checked_at", { ascending: true })
        .limit(2000);
      if (error) throw error;
      return data ?? [];
    },
  });

  const historyByDomain = useMemo(() => {
    const map = new Map<string, HistoryPoint[]>();
    for (const h of history ?? []) {
      const arr = map.get(h.domain_id) ?? [];
      arr.push(h as HistoryPoint);
      map.set(h.domain_id, arr);
    }
    return map;
  }, [history]);

  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState("");

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("domains").insert({ domain, user_id: user!.id });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Domain added");
      setOpen(false);
      setDomain("");
      qc.invalidateQueries({ queryKey: ["domains"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const checkOne = useServerFn(checkDomainReputation);
  const enqueueFn = useServerFn(enqueueDomainChecks);
  const queueStatusFn = useServerFn(getDomainCheckQueueStatus);
  const cancelJobFn = useServerFn(cancelDomainCheckJob);

  // Hintergrund-Warteschlange: pollt alle 5s solange pending/running > 0
  const queueStatus = useQuery({
    queryKey: ["domain-check-queue"],
    queryFn: () => queueStatusFn(),
    refetchInterval: (q) => {
      const counts = q.state.data?.counts;
      const active = (counts?.pending ?? 0) + (counts?.running ?? 0);
      return active > 0 ? 5000 : 30_000;
    },
  });
  // Wenn Jobs fertig werden → Domains & History neu laden
  const activeJobsRef = useRef(0);
  const currentActive =
    (queueStatus.data?.counts.pending ?? 0) + (queueStatus.data?.counts.running ?? 0);
  if (activeJobsRef.current > 0 && currentActive === 0) {
    qc.invalidateQueries({ queryKey: ["domains"] });
    qc.invalidateQueries({ queryKey: ["domain-history"] });
  }
  activeJobsRef.current = currentActive;

  const enqueueMutation = useMutation({
    mutationFn: (input: { domainIds: string[] | "all"; priority?: number }) =>
      enqueueFn({ data: input }),
    onSuccess: (res) => {
      if (res.enqueued === 0 && res.skipped > 0) {
        toast.info(`${res.skipped} Domain(s) sind bereits in der Warteschlange`);
      } else {
        toast.success(
          `${res.enqueued} Domain-Check(s) eingereiht${res.skipped ? `, ${res.skipped} übersprungen (bereits in Bearbeitung)` : ""}`,
        );
      }
      queueStatus.refetch();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const cancelJobMutation = useMutation({
    mutationFn: (id: string) => cancelJobFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Job abgebrochen");
      queueStatus.refetch();
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Live-Status pro Domain während eines Bulk-Runs.
  // waiting = in der Warteschlange, running = wird gerade geprüft,
  // ok = fertig (frisch oder aus Cache), failed = Fehler.
  type RunState = "waiting" | "running" | "ok" | "failed";
  type RunEntry = {
    state: RunState;
    cached?: boolean;
    score?: number;
    listed?: number;
    total?: number;
    error?: string;
    started_at?: number;
    finished_at?: number;
  };
  const [runStatus, setRunStatus] = useState<Record<string, RunEntry>>({});
  const [runActive, setRunActive] = useState(false);
  const cancelRef = useRef(false);

  const setEntry = useCallback((id: string, patch: Partial<RunEntry>) => {
    setRunStatus((s) => ({ ...s, [id]: { ...(s[id] ?? { state: "waiting" }), ...patch } }));
  }, []);

  // Client-seitige Orchestrierung: ruft pro Domain die Einzel-Prüfung parallel
  // (Concurrency 4) auf und aktualisiert jeden Status sofort, damit das UI
  // live sieht welche Domain läuft / fertig ist / fehlgeschlagen ist.
  const runChecks = useCallback(
    async (ids: string[], force: boolean) => {
      if (!ids.length || runActive) return;
      cancelRef.current = false;
      setRunActive(true);
      const init: Record<string, RunEntry> = {};
      for (const id of ids) init[id] = { state: "waiting" };
      setRunStatus((s) => ({ ...s, ...init }));

      const queue = [...ids];
      const concurrency = Math.min(4, queue.length);
      let okCount = 0;
      let failCount = 0;

      const worker = async () => {
        while (queue.length && !cancelRef.current) {
          const id = queue.shift();
          if (!id) break;
          setEntry(id, { state: "running", started_at: Date.now(), error: undefined });
          try {
            const res = await checkOne({ data: { id, force, quorum } });
            const s = res.status as {
              blacklist_summary?: { listed: number; total: number };
            };
            setEntry(id, {
              state: "ok",
              cached: res.cached,
              score: res.score,
              listed: s?.blacklist_summary?.listed,
              total: s?.blacklist_summary?.total,
              finished_at: Date.now(),
            });
            okCount++;
          } catch (e) {
            setEntry(id, {
              state: "failed",
              error: (e as Error).message ?? "Unbekannter Fehler",
              finished_at: Date.now(),
            });
            failCount++;
          }
        }
      };

      await Promise.all(Array.from({ length: concurrency }, () => worker()));
      setRunActive(false);
      qc.invalidateQueries({ queryKey: ["domains"] });
      qc.invalidateQueries({ queryKey: ["domain-history"] });
      toast.success(`${okCount} geprüft${failCount ? `, ${failCount} fehlgeschlagen` : ""}`, {
        duration: 5000,
      });
    },
    [checkOne, qc, runActive, setEntry, quorum],
  );

  const totalRun = Object.keys(runStatus).length;
  const doneRun = Object.values(runStatus).filter(
    (e) => e.state === "ok" || e.state === "failed",
  ).length;
  const failedIds = Object.entries(runStatus)
    .filter(([, e]) => e.state === "failed")
    .map(([id]) => id);
  const runningCount = Object.values(runStatus).filter((e) => e.state === "running").length;
  const progressPct = totalRun ? Math.round((doneRun / totalRun) * 100) : 0;

  const refreshOne = useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      checkOne({ data: { id, force, quorum } }),
    onSuccess: (res) => {
      if (res.cached) {
        toast.info(
          `Aus Cache (noch ${res.cooldown_seconds}s Cooldown) — Shift+Klick erzwingt frischen Check`,
        );
      } else {
        toast.success(`Reputation aktualisiert: ${res.score}/100`);
      }
      qc.invalidateQueries({ queryKey: ["domains"] });
      qc.invalidateQueries({ queryKey: ["domain-history"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("domains").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Domain gelöscht");
      qc.invalidateQueries({ queryKey: ["domains"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Reset pagination whenever filter/sort inputs or dataset size change
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [pageSize, filters, sortBy, sortDir, minScore, ageFilter, checkFilter, data?.length]);
  // Persist page size
  useEffect(() => {
    try {
      localStorage.setItem("domains-page-size", String(pageSize));
    } catch {
      /* ignore */
    }
  }, [pageSize]);
  // Auto load-more via IntersectionObserver — fires when sentinel scrolls into view
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((c) => c + pageSize);
        }
      },
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [pageSize, visibleCount]);

  return (
    <div className="p-8">
      <PageHeader
        title="Domains"
        description="Sending domains and their reputation."
        actions={
          <>
            {failedIds.length > 0 && !runActive && (
              <Button
                variant="outline"
                onClick={(e) => runChecks(failedIds, e.shiftKey)}
                title="Nur die fehlgeschlagenen Domains erneut prüfen — Shift+Klick erzwingt frischen Check"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
              >
                <AlertTriangle className="size-4 mr-1" />
                {failedIds.length} fehlgeschlagene erneut prüfen
              </Button>
            )}
            {runActive && (
              <Button
                variant="outline"
                onClick={() => {
                  cancelRef.current = true;
                }}
                title="Laufenden Bulk-Check abbrechen"
              >
                <X className="size-4 mr-1" />
                Abbrechen
              </Button>
            )}
            <Button
              variant="outline"
              onClick={(e) =>
                runChecks(
                  (data ?? []).map((d) => d.id),
                  e.shiftKey,
                )
              }
              title="Synchroner Bulk-Check (Concurrency 4) — bei vielen Domains lieber 'In Queue prüfen'"
              disabled={runActive || !data?.length}
            >
              <RefreshCw className={`size-4 mr-1 ${runActive ? "animate-spin" : ""}`} />
              Alle prüfen (sync)
            </Button>
            <Button
              variant="default"
              onClick={() => enqueueMutation.mutate({ domainIds: "all", priority: 0 })}
              title="Alle Domains in die Hintergrund-Warteschlange einreihen — kein Timeout-Risiko, läuft schonend mit Rate-Limit-Schutz"
              disabled={enqueueMutation.isPending || !data?.length}
            >
              <Clock className="size-4 mr-1" />
              In Queue prüfen
            </Button>
            <Button
              variant="outline"
              onClick={() => setQuorumOpen(true)}
              title="Quorum (Mindestübereinstimmung der Resolver) pro DNSBL einstellen"
            >
              <Settings2 className="size-4 mr-1" />
              Quorum
              <span className="ml-1 text-[10px] font-mono text-muted-foreground">
                {quorum.default}/{MAX_QUORUM}
                {quorum.perZone && Object.keys(quorum.perZone).length > 0
                  ? ` · ${Object.keys(quorum.perZone).length} Overrides`
                  : ""}
              </span>
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[image:var(--gradient-primary)] hover:opacity-90">
                  <Plus className="size-4 mr-1" /> Add domain
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add domain</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Domain</Label>
                    <Input
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      placeholder="yourdomain.com"
                    />
                  </div>
                  <Button
                    onClick={() => create.mutate()}
                    disabled={!domain || create.isPending}
                    className="w-full"
                  >
                    Add
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <QuorumSettingsDialog
              open={quorumOpen}
              onOpenChange={setQuorumOpen}
              value={quorum}
              onSave={saveQuorum}
            />
          </>
        }
      />

      <HelpPanel
        title="Hilfe: Deine Absende-Domains verwalten"
        what={
          <>
            Eine <b>Domain</b> ist der Teil hinter dem @-Zeichen, z.B. <code>deinefirma.de</code>.
            Hier siehst du alle Domains, von denen du Mails verschickst, und ob sie technisch sauber
            eingerichtet sind (DKIM, SPF, DMARC).
          </>
        }
        why={
          <>
            Ohne korrekt eingerichtete Domain landen deine Mails fast garantiert im Spam. Gmail und
            Outlook prüfen sehr genau, ob die Domain dich wirklich zum Versand berechtigt.
          </>
        }
        how={[
          <>
            <b>Neue Domain hinzufügen</b> — Eintrag entsteht.
          </>,
          <>
            System zeigt dir <b>DNS-Einträge</b>, die du bei deinem Domain-Anbieter (z.B. Strato,
            Ionos) eintragen musst.
          </>,
          <>
            5-30 Minuten warten, dann <b>Domain prüfen</b> klicken — grünes Häkchen bei DKIM, SPF,
            DMARC = perfekt.
          </>,
          <>
            Rotes Symbol? Auf die Domain klicken — dort steht genau, was fehlt und wie du es
            behebst.
          </>,
          <>Erst nach grünem Status Absender (Senders) auf dieser Domain anlegen.</>,
        ]}
        tips={[
          <>DNS-Änderungen brauchen manchmal bis zu 24 Stunden — Geduld.</>,
          <>Pro Domain max. 5-10 Absender, sonst wirkt es wie eine Spam-Farm.</>,
          <>
            Eigene Versand-Domain (z.B. <code>news.deinefirma.de</code>) ist besser als die
            Haupt-Domain — schützt deine normalen Mails.
          </>,
        ]}
        glossary={[
          {
            term: "DNS",
            def: "Adressbuch des Internets. Hier tragen wir Hinweise ein, die Gmail & Co. lesen, um deine Domain zu prüfen.",
          },
          {
            term: "DKIM",
            def: "Digitale Unterschrift unter jeder Mail. Beweist: die Mail kommt wirklich von deiner Domain.",
          },
          {
            term: "SPF",
            def: "Liste der Server, die in deinem Namen senden dürfen. Verhindert, dass Fremde sich als du ausgeben.",
          },
          {
            term: "DMARC",
            def: "Regel, was Empfänger tun sollen, wenn DKIM/SPF nicht passen (annehmen, in Spam, ablehnen).",
          },
        ]}
      />

      {totalRun > 0 && (
        <Card className="p-4 mb-4 bg-card/60 border-border">
          <div className="flex items-center justify-between text-xs mb-2">
            <div className="flex items-center gap-2">
              {runActive ? (
                <Loader2 className="size-4 animate-spin text-primary" />
              ) : (
                <Check className="size-4 text-success" />
              )}
              <span className="font-medium">
                {runActive ? "Prüfung läuft" : "Prüfung abgeschlossen"}
              </span>
              <span className="text-muted-foreground">
                {doneRun}/{totalRun} fertig{runningCount ? ` · ${runningCount} aktiv` : ""}
                {failedIds.length ? ` · ${failedIds.length} fehlgeschlagen` : ""}
              </span>
            </div>
            <span className="font-mono">{progressPct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                failedIds.length ? "bg-warning" : "bg-primary"
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </Card>
      )}

      {queueStatus.data &&
        queueStatus.data.counts.pending +
          queueStatus.data.counts.running +
          queueStatus.data.counts.failed >
          0 && (
          <Card className="p-4 mb-4 bg-card/60 border-border">
            <div className="flex items-center justify-between text-xs mb-1">
              <div className="flex items-center gap-2">
                {queueStatus.data.counts.running > 0 ? (
                  <Loader2 className="size-4 animate-spin text-primary" />
                ) : queueStatus.data.counts.pending > 0 ? (
                  <Clock className="size-4 text-muted-foreground" />
                ) : (
                  <AlertTriangle className="size-4 text-destructive" />
                )}
                <span className="font-medium">Hintergrund-Warteschlange</span>
                <span className="text-muted-foreground">
                  {queueStatus.data.counts.running} aktiv · {queueStatus.data.counts.pending}{" "}
                  wartend
                  {queueStatus.data.counts.failed
                    ? ` · ${queueStatus.data.counts.failed} fehlgeschlagen`
                    : ""}
                </span>
              </div>
              <span className="text-[10px] text-muted-foreground font-mono">
                Worker läuft jede Minute · Concurrency 3
              </span>
            </div>
            {queueStatus.data.counts.failed > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const ids = Array.from(
                      new Set(
                        queueStatus
                          .data!.jobs.filter((j) => j.status === "failed")
                          .map((j) => j.domain_id),
                      ),
                    );
                    enqueueMutation.mutate({ domainIds: ids, priority: 10 });
                  }}
                >
                  <RefreshCw className="size-3 mr-1" />
                  Fehlgeschlagene erneut einreihen
                </Button>
              </div>
            )}
          </Card>
        )}

      {!data?.length ? (
        <Card className="p-12 text-center bg-card/40 border-dashed">
          <Globe className="size-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">No domains yet</p>
        </Card>
      ) : (
        (() => {
          type Cat = "listed" | "unconfirmed" | "blocked" | "clean" | "err" | "unchecked";
          const categorize = (d: (typeof data)[number]): Set<Cat> => {
            const s = (d.blacklist_status ?? {}) as {
              checked_at?: string;
              audit?: {
                dnsbl?: Array<{
                  listed: boolean;
                  blocked: boolean;
                  error?: string;
                  quorum_hits?: number;
                  quorum_required?: number;
                }>;
              };
            };
            const cats = new Set<Cat>();
            if (!s.checked_at) {
              cats.add("unchecked");
              return cats;
            }
            const entries = s.audit?.dnsbl ?? [];
            if (!entries.length) {
              cats.add("unchecked");
              return cats;
            }
            for (const e of entries) {
              if (e.listed) cats.add("listed");
              else if (
                typeof e.quorum_hits === "number" &&
                e.quorum_hits > 0 &&
                typeof e.quorum_required === "number" &&
                e.quorum_hits < e.quorum_required
              )
                cats.add("unconfirmed");
              else if (e.blocked) cats.add("blocked");
              else if (e.error) cats.add("err");
              else cats.add("clean");
            }
            return cats;
          };
          const categorized = data.map((d) => ({ d, cats: categorize(d) }));
          const counts: Record<Cat, number> = {
            listed: 0,
            unconfirmed: 0,
            blocked: 0,
            clean: 0,
            err: 0,
            unchecked: 0,
          };
          for (const { cats } of categorized) for (const c of cats) counts[c]++;
          const STALE_MS = 7 * 24 * 60 * 60 * 1000;
          const now = Date.now();
          const baseFiltered =
            filters.size === 0
              ? categorized
              : categorized.filter(({ cats }) => {
                  for (const f of filters) if (cats.has(f)) return true;
                  return false;
                });
          const extraFiltered = baseFiltered.filter(({ d }) => {
            if ((d.reputation_score ?? 0) < minScore) return false;
            const s = (d.blacklist_status ?? {}) as any;
            const age = s.whois?.age_days as number | null | undefined;
            if (ageFilter !== "all") {
              if (age == null) {
                if (ageFilter !== "unknown") return false;
              } else {
                if (ageFilter === "unknown") return false;
                if (ageFilter === "new" && !(age < 30)) return false;
                if (ageFilter === "young" && !(age >= 30 && age < 365)) return false;
                if (ageFilter === "established" && !(age >= 365)) return false;
              }
            }
            if (checkFilter !== "all") {
              const checkedAt = s.checked_at ? new Date(s.checked_at).getTime() : null;
              if (checkFilter === "unchecked" && checkedAt != null) return false;
              if (checkFilter === "checked" && checkedAt == null) return false;
              if (checkFilter === "stale") {
                if (checkedAt == null || now - checkedAt < STALE_MS) return false;
              }
            }
            return true;
          });
          const filtered = [...extraFiltered].sort((a, b) => {
            const sa = (a.d.blacklist_status ?? {}) as any;
            const sb = (b.d.blacklist_status ?? {}) as any;
            let cmp = 0;
            if (sortBy === "score") {
              cmp = (a.d.reputation_score ?? 0) - (b.d.reputation_score ?? 0);
            } else if (sortBy === "age") {
              const av = (sa.whois?.age_days as number | null | undefined) ?? -1;
              const bv = (sb.whois?.age_days as number | null | undefined) ?? -1;
              cmp = av - bv;
            } else if (sortBy === "checked") {
              const av = sa.checked_at ? new Date(sa.checked_at).getTime() : 0;
              const bv = sb.checked_at ? new Date(sb.checked_at).getTime() : 0;
              cmp = av - bv;
            } else {
              cmp = a.d.domain.localeCompare(b.d.domain);
            }
            return sortDir === "asc" ? cmp : -cmp;
          });
          const chip = (k: Cat, label: string, cls: string) => {
            const active = filters.has(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleFilter(k)}
                className={`px-2 py-1 rounded-full text-xs font-medium border transition ${active ? `${cls} border-current` : "border-border text-muted-foreground hover:bg-muted"}`}
                title={`Domains mit mindestens einer ${label}-Zone ${active ? "ausblenden" : "anzeigen"}`}
              >
                {label} <span className="font-mono opacity-70">{counts[k]}</span>
              </button>
            );
          };
          return (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-2 text-xs">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">
                  Sortieren
                </span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  className="h-7 rounded-md border border-border bg-background px-2"
                  title="Sortierfeld"
                >
                  <option value="score">Score</option>
                  <option value="age">Alter (Domain)</option>
                  <option value="checked">Letzter Check</option>
                  <option value="domain">Name</option>
                </select>
                <button
                  type="button"
                  onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  className="h-7 px-2 rounded-md border border-border hover:bg-muted font-mono"
                  title={
                    sortDir === "asc"
                      ? "Aufsteigend — Klick für absteigend"
                      : "Absteigend — Klick für aufsteigend"
                  }
                >
                  {sortDir === "asc" ? "↑ asc" : "↓ desc"}
                </button>
                <span className="mx-1 h-4 w-px bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Score ≥
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={minScore}
                  onChange={(e) => setMinScore(Number(e.target.value))}
                  className="w-28 accent-primary"
                  title={`Mindest-Score: ${minScore}`}
                />
                <span className="font-mono w-8 text-right">{minScore}</span>
                <span className="mx-1 h-4 w-px bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Alter
                </span>
                <select
                  value={ageFilter}
                  onChange={(e) => setAgeFilter(e.target.value as typeof ageFilter)}
                  className="h-7 rounded-md border border-border bg-background px-2"
                  title="Domain-Alter (WHOIS)"
                >
                  <option value="all">alle</option>
                  <option value="new">neu (&lt;30 T)</option>
                  <option value="young">jung (30 T–1 J)</option>
                  <option value="established">etabliert (≥1 J)</option>
                  <option value="unknown">unbekannt</option>
                </select>
                <span className="mx-1 h-4 w-px bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Check
                </span>
                <select
                  value={checkFilter}
                  onChange={(e) => setCheckFilter(e.target.value as typeof checkFilter)}
                  className="h-7 rounded-md border border-border bg-background px-2"
                  title="Letzter Check-Status"
                >
                  <option value="all">alle</option>
                  <option value="checked">geprüft</option>
                  <option value="unchecked">ungeprüft</option>
                  <option value="stale">veraltet (&gt;7 T)</option>
                </select>
                {(minScore > 0 ||
                  ageFilter !== "all" ||
                  checkFilter !== "all" ||
                  sortBy !== "score" ||
                  sortDir !== "desc") && (
                  <button
                    type="button"
                    onClick={resetSortFilters}
                    className="h-7 px-2 rounded-md border border-border text-muted-foreground hover:bg-muted inline-flex items-center gap-1"
                    title="Sortierung & Filter zurücksetzen"
                  >
                    <X className="size-3" /> reset
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mb-3">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground mr-1">
                  Blacklist
                </span>
                {chip("listed", "LISTED", "bg-destructive/15 text-destructive")}
                {chip("unconfirmed", "UNCONFIRMED", "bg-amber-500/15 text-amber-500")}
                {chip("blocked", "BLOCKED", "bg-amber-500/10 text-amber-500")}
                {chip("clean", "clean", "bg-success/15 text-success")}
                {chip("err", "err", "bg-muted text-foreground")}
                {chip("unchecked", "ungeprüft", "bg-muted text-muted-foreground")}
                {filters.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setFilters(new Set())}
                    className="px-2 py-1 rounded-full text-xs border border-border text-muted-foreground hover:bg-muted inline-flex items-center gap-1"
                  >
                    <X className="size-3" /> reset
                  </button>
                )}
                <span className="ml-auto text-xs text-muted-foreground">
                  {Math.min(visibleCount, filtered.length)} / {filtered.length} sichtbar
                  {filtered.length !== data.length ? ` · ${data.length} gesamt` : ""}
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="h-7 rounded-md border border-border bg-background px-2 text-xs ml-2"
                  title="Seitengröße"
                >
                  <option value={10}>10/Seite</option>
                  <option value={20}>20/Seite</option>
                  <option value={50}>50/Seite</option>
                  <option value={100}>100/Seite</option>
                </select>
                <div className="inline-flex rounded-md border border-border overflow-hidden ml-2">
                  <button
                    type="button"
                    onClick={() => setView("cards")}
                    className={`px-2 py-1 text-xs inline-flex items-center gap-1 ${viewMode === "cards" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50"}`}
                    title="Kartenansicht"
                  >
                    <LayoutGrid className="size-3.5" /> Karten
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("list")}
                    className={`px-2 py-1 text-xs inline-flex items-center gap-1 border-l border-border ${viewMode === "list" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50"}`}
                    title="Listenansicht"
                  >
                    <ListIcon className="size-3.5" /> Liste
                  </button>
                </div>
              </div>
              {filtered.length === 0 ? (
                <Card className="p-8 text-center bg-card/40 border-dashed">
                  <p className="text-sm text-muted-foreground">
                    Keine Domains passen zum aktuellen Filter.
                  </p>
                </Card>
              ) : (
                (() => {
                  const visible = filtered.slice(0, visibleCount);
                  const hasMore = filtered.length > visible.length;
                  const remaining = filtered.length - visible.length;
                  const loadMore = (
                    <div ref={loadMoreRef} className="mt-6 flex flex-col items-center gap-2">
                      {hasMore ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setVisibleCount((c) => c + pageSize)}
                          >
                            Weitere {Math.min(remaining, pageSize)} laden ({remaining} verbleibend)
                          </Button>
                          <span className="text-[10px] text-muted-foreground">
                            Lädt automatisch beim Scrollen
                          </span>
                        </>
                      ) : filtered.length > pageSize ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setVisibleCount(pageSize)}
                          className="text-muted-foreground"
                        >
                          Alle {filtered.length} angezeigt · einklappen
                        </Button>
                      ) : null}
                    </div>
                  );
                  if (viewMode === "list") {
                    return (
                      <>
                        <DomainListView
                          rows={visible.map(({ d }) => d)}
                          runStatus={runStatus}
                          busyId={refreshOne.isPending ? refreshOne.variables?.id : undefined}
                          onRefresh={(id) => refreshOne.mutate({ id, force: true })}
                          onDelete={(id, name) => {
                            if (confirm(`Domain "${name}" wirklich löschen?`)) remove.mutate(id);
                          }}
                          onOpenDetail={(id) => setDetailId(id)}
                        />
                        {loadMore}
                      </>
                    );
                  }
                  return (
                    <>
                      <div className="grid md:grid-cols-2 gap-6">
                        {visible.map(({ d }) => {
                          const ok = d.reputation_score >= 70;
                          const status = (d.blacklist_status ?? {}) as {
                            checked_at?: string;
                            mx?: boolean;
                            spf?: boolean;
                            spf_record?: string | null;
                            dmarc?: boolean;
                            dmarc_policy?: string | null;
                            dmarc_record?: string | null;
                            blacklists?: Record<string, boolean>;
                            notes?: string[];
                            breakdown?: { label: string; delta: number }[];
                            blacklist_summary?: {
                              total: number;
                              listed: number;
                              listed_zones: string[];
                            };
                            mx_hosts?: { host: string; priority: number }[];
                            a_records?: string[];
                            ns_records?: string[];
                            ptr_records?: { ip: string; ptr: string | null }[];
                            dkim?: { selector: string; found: boolean; record: string | null }[];
                            mta_sts?: { found: boolean; mode: string | null };
                            tls_rpt?: { found: boolean; record: string | null };
                            bimi?: { found: boolean; record: string | null };
                            spf_analysis?: {
                              all_mechanism: string | null;
                              includes: string[];
                              lookups_estimate: number;
                              too_permissive: boolean;
                            } | null;
                            dmarc_analysis?: {
                              rua: string | null;
                              ruf: string | null;
                              pct: number | null;
                              sp: string | null;
                              adkim: string | null;
                              aspf: string | null;
                            } | null;
                            recommendations?: {
                              severity: "critical" | "high" | "medium" | "low" | "info";
                              category: string;
                              title: string;
                              why: string;
                              how: string;
                              example?: string;
                              link?: { label: string; url: string };
                            }[];
                            whois?: {
                              registrar: string | null;
                              registered_at: string | null;
                              expires_at: string | null;
                              age_days: number | null;
                              status: string[];
                              source: string | null;
                              error?: string;
                              attempts?: {
                                endpoint: string;
                                host: string;
                                http_status: number | null;
                                ok: boolean;
                                error: string | null;
                                timed_out: boolean;
                                duration_ms: number;
                                attempt: number;
                                retried_after_ms?: number;
                              }[];
                            };
                            audit?: {
                              dnsbl?: {
                                zone: string;
                                target: string;
                                kind: "domain" | "ip";
                                resolver: string | null;
                                raw_answers: string[];
                                listed: boolean;
                                blocked: boolean;
                                resolvers_tried: string[];
                                error?: string;
                                quorum_required?: number;
                                quorum_hits?: number;
                                quorum_confirmed?: boolean;
                                per_resolver?: {
                                  resolver: string;
                                  answers: string[];
                                  hit: boolean;
                                  blocked: boolean;
                                  error?: string;
                                }[];
                              }[];
                              resolvers_blocked?: string[];
                            };
                          };
                          const checked = !!status.checked_at;
                          const busy = refreshOne.isPending && refreshOne.variables?.id === d.id;
                          const run = runStatus[d.id];
                          return (
                            <Card key={d.id} className="p-5 bg-card/60 border-border">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                      type="button"
                                      onClick={() => setDetailId(d.id)}
                                      className="font-mono font-semibold text-left hover:underline underline-offset-2 hover:text-primary transition"
                                      title="Vollständige Details anzeigen"
                                    >
                                      {d.domain}
                                    </button>
                                    {run && <RunPill entry={run} />}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {checked
                                      ? `Geprüft ${new Date(status.checked_at!).toLocaleString()}`
                                      : "Noch nicht geprüft"}
                                  </div>
                                  {run?.state === "failed" && run.error && (
                                    <div className="mt-1 text-xs text-destructive break-all">
                                      {run.error}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => refreshOne.mutate({ id: d.id, force: true })}
                                    disabled={busy}
                                    title="Reputation jetzt frisch prüfen (DNS-Cache umgehen)"
                                  >
                                    <RefreshCw className={`size-4 ${busy ? "animate-spin" : ""}`} />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    title="Domain löschen"
                                    onClick={() => {
                                      if (confirm(`Domain "${d.domain}" wirklich löschen?`)) {
                                        remove.mutate(d.id);
                                      }
                                    }}
                                  >
                                    <Trash2 className="size-4 text-destructive" />
                                  </Button>
                                  {ok ? (
                                    <ShieldCheck className="size-5 text-success" />
                                  ) : (
                                    <ShieldAlert className="size-5 text-warning" />
                                  )}
                                </div>
                              </div>
                              <div className="mt-5">
                                <div className="flex items-baseline justify-between">
                                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                                    Reputation
                                  </span>
                                  <span className="text-2xl font-semibold">
                                    {d.reputation_score}
                                    <span className="text-xs text-muted-foreground">/100</span>
                                  </span>
                                </div>
                                <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                                  <div
                                    className={`h-full ${ok ? "bg-success" : "bg-warning"}`}
                                    style={{ width: `${d.reputation_score}%` }}
                                  />
                                </div>
                              </div>

                              {checked && (
                                <div className="mt-4 space-y-2 text-xs">
                                  <div className="grid grid-cols-3 gap-2">
                                    <CheckBadge label="MX" ok={!!status.mx} />
                                    <CheckBadge label="SPF" ok={!!status.spf} />
                                    <CheckBadge
                                      label={`DMARC${status.dmarc_policy ? ` (${status.dmarc_policy})` : ""}`}
                                      ok={!!status.dmarc}
                                    />
                                  </div>
                                  {status.blacklist_summary && (
                                    <div
                                      className={`rounded-md border p-2 flex items-center justify-between ${
                                        status.blacklist_summary.listed === 0
                                          ? "border-success/40 bg-success/10"
                                          : "border-destructive/40 bg-destructive/10"
                                      }`}
                                    >
                                      <span className="uppercase tracking-wider text-[10px] text-muted-foreground">
                                        Blacklist-Status
                                      </span>
                                      <span className="font-semibold">
                                        {status.blacklist_summary.listed === 0
                                          ? `✓ Sauber (0/${status.blacklist_summary.total})`
                                          : `🚨 ${status.blacklist_summary.listed}/${status.blacklist_summary.total} gelistet`}
                                      </span>
                                    </div>
                                  )}
                                  {status.blacklists &&
                                    Object.keys(status.blacklists).length > 0 && (
                                      <details className="rounded-md border border-border bg-muted/20 p-2">
                                        <summary className="cursor-pointer uppercase tracking-wider text-[10px] text-muted-foreground">
                                          Alle {Object.keys(status.blacklists).length} Blacklists
                                          anzeigen
                                        </summary>
                                        <div className="mt-2 grid grid-cols-2 gap-1">
                                          {Object.entries(status.blacklists).map(([z, listed]) => (
                                            <CheckBadge
                                              key={z}
                                              label={z}
                                              ok={!listed}
                                              okText="clean"
                                              badText="LISTED"
                                            />
                                          ))}
                                        </div>
                                      </details>
                                    )}
                                  {status.breakdown && status.breakdown.length > 0 && (
                                    <details className="rounded-md border border-border bg-muted/20 p-2">
                                      <summary className="cursor-pointer uppercase tracking-wider text-[10px] text-muted-foreground">
                                        Score-Aufschlüsselung
                                      </summary>
                                      <ul className="mt-2 space-y-0.5">
                                        {status.breakdown.map((b, i) => (
                                          <li key={i} className="flex justify-between">
                                            <span className="text-muted-foreground">{b.label}</span>
                                            <span
                                              className={`font-mono font-semibold ${
                                                b.delta > 0
                                                  ? "text-success"
                                                  : b.delta < 0
                                                    ? "text-destructive"
                                                    : "text-muted-foreground"
                                              }`}
                                            >
                                              {b.delta > 0 ? "+" : ""}
                                              {b.delta}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  )}
                                  {status.recommendations && status.recommendations.length > 0 && (
                                    <details
                                      className="rounded-md border border-primary/40 bg-primary/5 p-2"
                                      open
                                    >
                                      <summary className="cursor-pointer uppercase tracking-wider text-[10px] text-primary font-semibold">
                                        🛠 {status.recommendations.length} Empfehlung
                                        {status.recommendations.length === 1 ? "" : "en"} zum
                                        Verbessern
                                      </summary>
                                      <div className="mt-2 space-y-2">
                                        {status.recommendations.map((r, i) => (
                                          <div
                                            key={i}
                                            className="rounded border border-border bg-background/40 p-2"
                                          >
                                            <div className="flex items-center gap-2">
                                              <SeverityBadge sev={r.severity} />
                                              <span className="font-semibold">{r.title}</span>
                                            </div>
                                            <div className="mt-1 text-muted-foreground">
                                              {r.why}
                                            </div>
                                            <div className="mt-1">
                                              <span className="text-foreground font-medium">
                                                Fix:
                                              </span>{" "}
                                              {r.how}
                                            </div>
                                            {r.example && (
                                              <pre className="mt-1 overflow-x-auto rounded bg-muted/40 p-1.5 font-mono text-[10px] leading-tight">
                                                {r.example}
                                              </pre>
                                            )}
                                            {r.link && (
                                              <a
                                                href={r.link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-1 inline-block text-primary underline underline-offset-2"
                                              >
                                                {r.link.label} ↗
                                              </a>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  )}
                                  {(status.spf_record ||
                                    status.dmarc_record ||
                                    status.mx_hosts?.length) && (
                                    <details className="rounded-md border border-border bg-muted/20 p-2">
                                      <summary className="cursor-pointer uppercase tracking-wider text-[10px] text-muted-foreground">
                                        DNS-Records (roh)
                                      </summary>
                                      <div className="mt-2 space-y-1.5 text-[11px]">
                                        {status.mx_hosts && status.mx_hosts.length > 0 && (
                                          <div>
                                            <div className="text-muted-foreground">MX</div>
                                            {status.mx_hosts.map((m, i) => (
                                              <div key={i} className="font-mono">
                                                {m.priority} {m.host}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {status.a_records && status.a_records.length > 0 && (
                                          <div>
                                            <div className="text-muted-foreground">A</div>
                                            {status.a_records.map((a, i) => {
                                              const ptr = status.ptr_records?.find(
                                                (p) => p.ip === a,
                                              )?.ptr;
                                              return (
                                                <div key={i} className="font-mono">
                                                  {a}
                                                  {ptr ? (
                                                    <span className="text-muted-foreground">
                                                      {" "}
                                                      → {ptr}
                                                    </span>
                                                  ) : (
                                                    <span className="text-destructive">
                                                      {" "}
                                                      (kein PTR)
                                                    </span>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {status.ns_records && status.ns_records.length > 0 && (
                                          <div>
                                            <div className="text-muted-foreground">NS</div>
                                            {status.ns_records.map((n, i) => (
                                              <div key={i} className="font-mono">
                                                {n}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {status.spf_record && (
                                          <div>
                                            <div className="text-muted-foreground">SPF</div>
                                            <div className="font-mono break-all">
                                              {status.spf_record}
                                            </div>
                                            {status.spf_analysis && (
                                              <div className="text-muted-foreground">
                                                {status.spf_analysis.lookups_estimate} DNS-Lookups ·
                                                all=
                                                {status.spf_analysis.all_mechanism ?? "—"}
                                                {status.spf_analysis.includes.length > 0 &&
                                                  ` · ${status.spf_analysis.includes.length} includes`}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        {status.dmarc_record && (
                                          <div>
                                            <div className="text-muted-foreground">DMARC</div>
                                            <div className="font-mono break-all">
                                              {status.dmarc_record}
                                            </div>
                                            {status.dmarc_analysis && (
                                              <div className="text-muted-foreground">
                                                pct={status.dmarc_analysis.pct ?? "100"} · sp=
                                                {status.dmarc_analysis.sp ?? "—"} · adkim=
                                                {status.dmarc_analysis.adkim ?? "r"} · aspf=
                                                {status.dmarc_analysis.aspf ?? "r"}
                                                {status.dmarc_analysis.rua && (
                                                  <div className="break-all">
                                                    rua: {status.dmarc_analysis.rua}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        {status.dkim && status.dkim.some((k) => k.found) && (
                                          <div>
                                            <div className="text-muted-foreground">DKIM</div>
                                            {status.dkim
                                              .filter((k) => k.found)
                                              .map((k, i) => (
                                                <div key={i} className="font-mono break-all">
                                                  <span className="text-primary">{k.selector}</span>
                                                  : {k.record?.slice(0, 80)}…
                                                </div>
                                              ))}
                                          </div>
                                        )}
                                        <div className="grid grid-cols-3 gap-2 pt-1">
                                          <CheckBadge
                                            label="MTA-STS"
                                            ok={!!status.mta_sts?.found}
                                          />
                                          <CheckBadge
                                            label="TLS-RPT"
                                            ok={!!status.tls_rpt?.found}
                                          />
                                          <CheckBadge label="BIMI" ok={!!status.bimi?.found} />
                                        </div>
                                      </div>
                                    </details>
                                  )}
                                  {status.audit?.dnsbl &&
                                    status.audit.dnsbl.length > 0 &&
                                    (() => {
                                      const entries = status.audit.dnsbl;
                                      const listed = entries.filter((e) => e.listed).length;
                                      const unconfirmed = entries.filter(
                                        (e) =>
                                          !e.listed &&
                                          typeof e.quorum_hits === "number" &&
                                          e.quorum_hits > 0,
                                      ).length;
                                      const blocked = entries.filter(
                                        (e) => !e.listed && e.blocked,
                                      ).length;
                                      const errs = entries.filter(
                                        (e) => !e.listed && !e.blocked && e.error,
                                      ).length;
                                      const clean =
                                        entries.length - listed - unconfirmed - blocked - errs;
                                      const allResolvers = Array.from(
                                        new Set(entries.flatMap((e) => e.resolvers_tried ?? [])),
                                      );
                                      return (
                                        <div className="rounded-md border border-border bg-muted/20 p-2 space-y-2">
                                          <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <span className="uppercase tracking-wider text-[10px] text-muted-foreground">
                                              DNSBL Audit · {entries.length} Lookups
                                            </span>
                                            <div className="flex items-center gap-1.5 text-[10px] font-mono">
                                              <span className="px-1.5 py-0.5 rounded bg-destructive/15 text-destructive">
                                                {listed} listed
                                              </span>
                                              <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">
                                                {unconfirmed} unconfirmed
                                              </span>
                                              <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500">
                                                {blocked} blocked
                                              </span>
                                              <span className="px-1.5 py-0.5 rounded bg-success/15 text-success">
                                                {clean} clean
                                              </span>
                                              {errs > 0 && (
                                                <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                                  {errs} err
                                                </span>
                                              )}
                                              <span className="mx-1 text-muted-foreground/40">
                                                |
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  exportDnsblAudit(
                                                    d.domain,
                                                    entries,
                                                    {
                                                      checked_at: status.checked_at,
                                                      resolvers_blocked:
                                                        status.audit?.resolvers_blocked,
                                                    },
                                                    "json",
                                                  )
                                                }
                                                className="px-1.5 py-0.5 rounded border border-border hover:bg-muted inline-flex items-center gap-1"
                                                title="DNSBL-Audit als JSON exportieren"
                                              >
                                                <Download className="size-3" /> JSON
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  exportDnsblAudit(
                                                    d.domain,
                                                    entries,
                                                    {
                                                      checked_at: status.checked_at,
                                                      resolvers_blocked:
                                                        status.audit?.resolvers_blocked,
                                                    },
                                                    "csv",
                                                  )
                                                }
                                                className="px-1.5 py-0.5 rounded border border-border hover:bg-muted inline-flex items-center gap-1"
                                                title="DNSBL-Audit als CSV exportieren"
                                              >
                                                <Download className="size-3" /> CSV
                                              </button>
                                            </div>
                                          </div>
                                          <div className="text-[10px] text-muted-foreground font-mono">
                                            Resolver-Kette:{" "}
                                            {allResolvers.length ? allResolvers.join(" → ") : "—"}
                                            {status.audit.resolvers_blocked &&
                                              status.audit.resolvers_blocked.length > 0 && (
                                                <span>
                                                  {" "}
                                                  ·{" "}
                                                  <span className="text-amber-500">
                                                    {status.audit.resolvers_blocked.length}
                                                  </span>{" "}
                                                  Zonen vollständig geblockt
                                                </span>
                                              )}
                                          </div>
                                          <DnsblAuditTable entries={entries} />
                                          <div className="text-[9px] text-muted-foreground leading-relaxed">
                                            <b>HIT</b> = Resolver liefert echtes Listing
                                            (127.0.0.2+) · <b>BLOCK</b> = Resolver vom DNSBL
                                            geblockt (127.255.x / 127.0.0.1) · <b>NX</b> = NXDOMAIN,
                                            keine Listung · <b>ERR</b> = Netzwerkfehler
                                            <br />
                                            <b>LISTED</b> = ≥2 Resolver bestätigen Hit (Quorum) ·{" "}
                                            <b>UNCONFIRMED</b> = nur 1 Resolver meldet Hit, nicht
                                            gewertet · <b>BLOCKED</b> = alle Resolver geblockt ·{" "}
                                            <b>clean</b> = keine Listung
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  {status.whois && (
                                    <div className="rounded-md border border-border bg-muted/20 p-2 space-y-1">
                                      <div className="flex items-center justify-between">
                                        <span className="uppercase tracking-wider text-[10px] text-muted-foreground">
                                          WHOIS / RDAP
                                        </span>
                                        {status.whois.source && (
                                          <span className="text-[10px] text-muted-foreground">
                                            {status.whois.source}
                                          </span>
                                        )}
                                      </div>
                                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                                        <div className="text-muted-foreground">Registrar</div>
                                        <div
                                          className="truncate text-right font-medium"
                                          title={status.whois.registrar ?? undefined}
                                        >
                                          {status.whois.registrar ?? "—"}
                                        </div>
                                        <div className="text-muted-foreground">Alter</div>
                                        <div className="text-right font-medium">
                                          {status.whois.age_days != null
                                            ? `${status.whois.age_days} Tage`
                                            : "—"}
                                        </div>
                                        <div className="text-muted-foreground">Registriert</div>
                                        <div className="text-right">
                                          {status.whois.registered_at
                                            ? new Date(
                                                status.whois.registered_at,
                                              ).toLocaleDateString()
                                            : "—"}
                                        </div>
                                        <div className="text-muted-foreground">Läuft ab</div>
                                        <div className="text-right">
                                          {status.whois.expires_at
                                            ? new Date(status.whois.expires_at).toLocaleDateString()
                                            : "—"}
                                        </div>
                                      </div>
                                      {status.whois.error && (
                                        <div className="mt-1 rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] text-destructive">
                                          Abbruchgrund: {status.whois.error}
                                        </div>
                                      )}
                                      {status.whois.attempts &&
                                        status.whois.attempts.length > 0 && (
                                          <details className="mt-1">
                                            <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground">
                                              RDAP-Versuche ({status.whois.attempts.length})
                                            </summary>
                                            <ul className="mt-1 space-y-0.5 text-[11px] font-mono">
                                              {status.whois.attempts.map((a, i) => (
                                                <li
                                                  key={i}
                                                  className={
                                                    a.ok
                                                      ? "text-emerald-600"
                                                      : a.timed_out
                                                        ? "text-amber-600"
                                                        : "text-destructive"
                                                  }
                                                >
                                                  {a.ok ? "✓" : a.timed_out ? "⏱" : "✗"} #
                                                  {a.attempt} {a.host} —{" "}
                                                  {a.ok
                                                    ? `HTTP ${a.http_status} OK`
                                                    : (a.error ?? "Fehler")}{" "}
                                                  <span className="text-muted-foreground">
                                                    ({a.duration_ms}ms
                                                    {a.retried_after_ms != null
                                                      ? `, Retry in ${a.retried_after_ms}ms`
                                                      : ""}
                                                    )
                                                  </span>
                                                </li>
                                              ))}
                                            </ul>
                                          </details>
                                        )}
                                    </div>
                                  )}
                                  {status.notes && status.notes.length > 0 && (
                                    <ul className="text-muted-foreground space-y-0.5 pt-1">
                                      {status.notes.map((n, i) => (
                                        <li key={i}>• {n}</li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              )}
                              <ScoreHistory
                                points={historyByDomain.get(d.id) ?? []}
                                currentScore={d.reputation_score}
                              />
                            </Card>
                          );
                        })}
                      </div>
                      {loadMore}
                    </>
                  );
                })()
              )}
            </>
          );
        })()
      )}
      <DomainDetailDrawer
        domain={(data ?? []).find((d) => d.id === detailId) ?? null}
        onOpenChange={(open) => {
          if (!open) setDetailId(null);
        }}
        onRefresh={(id) => refreshOne.mutate({ id, force: true })}
        busy={refreshOne.isPending && refreshOne.variables?.id === detailId}
      />
    </div>
  );
}

type DnsblAuditEntry = {
  zone: string;
  target: string;
  kind: "domain" | "ip";
  resolver: string | null;
  raw_answers: string[];
  listed: boolean;
  blocked: boolean;
  resolvers_tried: string[];
  error?: string;
  quorum_required?: number;
  quorum_hits?: number;
  quorum_confirmed?: boolean;
  per_resolver?: {
    resolver: string;
    answers: string[];
    hit: boolean;
    blocked: boolean;
    error?: string;
  }[];
};

function DnsblAuditTable({ entries }: { entries: DnsblAuditEntry[] }) {
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState<DnsblAuditEntry | null>(null);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? entries.filter((e) => {
        if (e.zone.toLowerCase().includes(q)) return true;
        if (e.target.toLowerCase().includes(q)) return true;
        if (e.resolver && e.resolver.toLowerCase().includes(q)) return true;
        if (e.resolvers_tried.some((r) => r.toLowerCase().includes(q))) return true;
        if (e.raw_answers.some((a) => a.toLowerCase().includes(q))) return true;
        if (
          e.per_resolver?.some(
            (p) =>
              p.resolver.toLowerCase().includes(q) ||
              p.answers.some((a) => a.toLowerCase().includes(q)) ||
              (p.error?.toLowerCase().includes(q) ?? false),
          )
        )
          return true;
        if (e.error && e.error.toLowerCase().includes(q)) return true;
        return false;
      })
    : entries;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Search className="size-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zone, Resolver oder Raw-Antwort suchen…"
            className="w-full h-7 pl-7 pr-7 text-xs rounded border border-border bg-background font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              title="Suche leeren"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
        <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">
          {filtered.length}/{entries.length}
        </span>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-[10px] font-mono">
          <thead className="text-muted-foreground">
            <tr className="text-left sticky top-0 bg-muted/40 backdrop-blur">
              <th className="pr-2 py-0.5">Zone</th>
              <th className="pr-2 py-0.5">Ziel</th>
              <th className="pr-2 py-0.5">Resolverkette · Raw-Antworten</th>
              <th className="py-0.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-3 text-center text-muted-foreground">
                  Keine Treffer für "{query}".
                </td>
              </tr>
            ) : (
              filtered.map((e, i) => (
                <tr
                  key={i}
                  onClick={() => setDetail(e)}
                  className={
                    "border-t border-border/40 align-top cursor-pointer hover:bg-muted/40 transition " +
                    (e.listed ? "bg-destructive/5" : e.blocked ? "bg-amber-500/5" : "")
                  }
                  title="Klick für Detailansicht"
                >
                  <td className="pr-2 py-1 whitespace-nowrap text-primary underline-offset-2 hover:underline">
                    {e.zone}
                  </td>
                  <td className="pr-2 py-1 text-muted-foreground break-all">{e.target}</td>
                  <td className="pr-2 py-1 text-muted-foreground break-all">
                    {e.per_resolver && e.per_resolver.length > 0 ? (
                      <div className="space-y-0.5">
                        {e.per_resolver.map((p, j) => {
                          const tag = p.hit
                            ? "HIT"
                            : p.blocked
                              ? "BLOCK"
                              : p.error
                                ? "ERR"
                                : p.answers.length
                                  ? "ANS"
                                  : "NX";
                          const tagColor = p.hit
                            ? "text-destructive"
                            : p.blocked
                              ? "text-amber-500"
                              : p.error
                                ? "text-muted-foreground"
                                : "text-success";
                          return (
                            <div key={j} className="flex gap-1.5">
                              <span className={"shrink-0 w-12 " + tagColor}>[{tag}]</span>
                              <span className="shrink-0 text-foreground w-16">{p.resolver}</span>
                              <span className="text-muted-foreground break-all">
                                {p.error
                                  ? p.error
                                  : p.answers.length
                                    ? p.answers.join(", ")
                                    : "NXDOMAIN / empty"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-0.5">
                        <div>
                          {e.resolver ?? "—"}:{" "}
                          {e.raw_answers.length ? e.raw_answers.join(", ") : "—"}
                        </div>
                        {e.resolvers_tried.length > 1 && (
                          <div className="text-muted-foreground">
                            getestet: {e.resolvers_tried.join(" → ")}
                          </div>
                        )}
                      </div>
                    )}
                    {e.error && <div className="text-destructive/80 mt-0.5">⚠ {e.error}</div>}
                  </td>
                  <td className="py-1 whitespace-nowrap">
                    {e.listed ? (
                      <span className="text-destructive font-semibold">
                        LISTED
                        {typeof e.quorum_hits === "number" &&
                          typeof e.quorum_required === "number" && (
                            <span className="text-muted-foreground font-normal">
                              {" "}
                              ({e.quorum_hits}/{e.quorum_required})
                            </span>
                          )}
                      </span>
                    ) : typeof e.quorum_hits === "number" &&
                      e.quorum_hits > 0 &&
                      typeof e.quorum_required === "number" &&
                      e.quorum_hits < e.quorum_required ? (
                      <span className="text-amber-500" title="Hit ohne Quorum — nicht bestätigt">
                        UNCONFIRMED ({e.quorum_hits}/{e.quorum_required})
                      </span>
                    ) : e.blocked ? (
                      <span className="text-amber-500">BLOCKED</span>
                    ) : e.error ? (
                      <span className="text-muted-foreground">ERR</span>
                    ) : (
                      <span className="text-success">clean</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <DnsblDetailDialog entry={detail} onClose={() => setDetail(null)} />
    </>
  );
}

function DnsblDetailDialog({
  entry,
  onClose,
}: {
  entry: DnsblAuditEntry | null;
  onClose: () => void;
}) {
  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("In Zwischenablage kopiert"),
      () => toast.error("Kopieren fehlgeschlagen"),
    );
  };
  return (
    <Dialog
      open={!!entry}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-3xl">
        {entry && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span className="font-mono">{entry.zone}</span>
                {entry.listed ? (
                  <span className="text-xs px-2 py-0.5 rounded bg-destructive/15 text-destructive font-semibold">
                    LISTED
                    {typeof entry.quorum_hits === "number" &&
                      typeof entry.quorum_required === "number" &&
                      ` (${entry.quorum_hits}/${entry.quorum_required})`}
                  </span>
                ) : typeof entry.quorum_hits === "number" &&
                  entry.quorum_hits > 0 &&
                  typeof entry.quorum_required === "number" &&
                  entry.quorum_hits < entry.quorum_required ? (
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-500 font-semibold">
                    UNCONFIRMED ({entry.quorum_hits}/{entry.quorum_required})
                  </span>
                ) : entry.blocked ? (
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/15 text-amber-500 font-semibold">
                    BLOCKED
                  </span>
                ) : entry.error ? (
                  <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground font-semibold">
                    ERR
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded bg-success/15 text-success font-semibold">
                    clean
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-[100px_1fr] gap-x-3 gap-y-1 text-xs">
                <div className="text-muted-foreground">Ziel</div>
                <div className="font-mono break-all flex items-center gap-2">
                  <span>{entry.target}</span>
                  <button
                    onClick={() => copy(entry.target)}
                    className="text-muted-foreground hover:text-foreground"
                    title="Kopieren"
                  >
                    <Download className="size-3 rotate-180" />
                  </button>
                </div>
                <div className="text-muted-foreground">Typ</div>
                <div className="font-mono">{entry.kind === "ip" ? "IP-DNSBL" : "Domain-DNSBL"}</div>
                <div className="text-muted-foreground">Resolver getestet</div>
                <div className="font-mono break-all">
                  {entry.resolvers_tried.length ? entry.resolvers_tried.join(" → ") : "—"}
                </div>
                {typeof entry.quorum_required === "number" && (
                  <>
                    <div className="text-muted-foreground">Quorum</div>
                    <div className="font-mono">
                      {entry.quorum_hits ?? 0}/{entry.quorum_required} Hits ·
                      {entry.quorum_confirmed ? " bestätigt" : " nicht bestätigt"}
                    </div>
                  </>
                )}
                {entry.error && (
                  <>
                    <div className="text-muted-foreground">Fehler</div>
                    <div className="font-mono text-destructive break-all">{entry.error}</div>
                  </>
                )}
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                  Resolverkette · Raw-Antworten
                </div>
                {entry.per_resolver && entry.per_resolver.length > 0 ? (
                  <div className="space-y-2">
                    {entry.per_resolver.map((p, j) => {
                      const tag = p.hit
                        ? "HIT"
                        : p.blocked
                          ? "BLOCK"
                          : p.error
                            ? "ERR"
                            : p.answers.length
                              ? "ANS"
                              : "NX";
                      const tagCls = p.hit
                        ? "bg-destructive/15 text-destructive"
                        : p.blocked
                          ? "bg-amber-500/15 text-amber-500"
                          : p.error
                            ? "bg-muted text-muted-foreground"
                            : p.answers.length
                              ? "bg-primary/15 text-primary"
                              : "bg-success/15 text-success";
                      return (
                        <div key={j} className="rounded-md border border-border bg-muted/20 p-3">
                          <div className="flex items-center justify-between gap-2 mb-1.5">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded ${tagCls}`}
                              >
                                [{tag}]
                              </span>
                              <span className="font-mono text-sm font-medium">{p.resolver}</span>
                            </div>
                            {(p.answers.length > 0 || p.error) && (
                              <button
                                onClick={() => copy(p.error ? p.error : p.answers.join("\n"))}
                                className="text-[10px] text-muted-foreground hover:text-foreground"
                                title="Raw-Antwort kopieren"
                              >
                                copy
                              </button>
                            )}
                          </div>
                          {p.error ? (
                            <div className="font-mono text-xs text-destructive/90 break-all">
                              {p.error}
                            </div>
                          ) : p.answers.length > 0 ? (
                            <ul className="font-mono text-xs space-y-0.5">
                              {p.answers.map((a, k) => (
                                <li key={k} className="break-all">
                                  {a}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div className="font-mono text-xs text-muted-foreground">
                              NXDOMAIN / leere Antwort
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-md border border-border bg-muted/20 p-3 font-mono text-xs">
                    <div className="font-medium mb-1">{entry.resolver ?? "—"}</div>
                    {entry.raw_answers.length > 0 ? (
                      <ul className="space-y-0.5">
                        {entry.raw_answers.map((a, k) => (
                          <li key={k} className="break-all">
                            {a}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-muted-foreground">— keine Antworten —</div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy(JSON.stringify(entry, null, 2))}
                >
                  JSON kopieren
                </Button>
                <Button size="sm" onClick={onClose}>
                  Schließen
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CheckBadge({
  label,
  ok,
  okText = "OK",
  badText = "fehlt",
}: {
  label: string;
  ok: boolean;
  okText?: string;
  badText?: string;
}) {
  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 rounded border ${
        ok
          ? "border-success/30 bg-success/10 text-success"
          : "border-destructive/30 bg-destructive/10 text-destructive"
      }`}
    >
      {ok ? <Check className="size-3" /> : <X className="size-3" />}
      <span className="font-medium truncate">{label}</span>
      <span className="ml-auto opacity-70">{ok ? okText : badText}</span>
    </div>
  );
}

function SeverityBadge({ sev }: { sev: "critical" | "high" | "medium" | "low" | "info" }) {
  const cls = {
    critical: "bg-destructive/20 text-destructive border-destructive/40",
    high: "bg-warning/20 text-warning border-warning/40",
    medium: "bg-primary/20 text-primary border-primary/40",
    low: "bg-muted text-muted-foreground border-border",
    info: "bg-muted text-muted-foreground border-border",
  }[sev];
  const label = {
    critical: "KRITISCH",
    high: "HOCH",
    medium: "MITTEL",
    low: "NIEDRIG",
    info: "INFO",
  }[sev];
  return (
    <span
      className={`px-1.5 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}

type HistoryPoint = {
  domain_id: string;
  score: number;
  checked_at: string;
  blacklists: Record<string, boolean> | null;
  notes: string[] | null;
  dmarc_policy: string | null;
};

function ScoreHistory({ points, currentScore }: { points: HistoryPoint[]; currentScore: number }) {
  if (!points.length) {
    return (
      <div className="mt-4 pt-3 border-t border-border text-[11px] text-muted-foreground">
        Noch keine Verlaufsdaten — nach der nächsten Prüfung erscheint hier ein Score-Trend.
      </div>
    );
  }

  // Pad with current value if last history is older than current
  const series = points.slice(-30);
  const W = 260;
  const H = 56;
  const PAD = 4;
  const xs = series.map((_, i) =>
    series.length === 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (series.length - 1),
  );
  const y = (s: number) => H - PAD - (s / 100) * (H - PAD * 2);
  const path = series
    .map((p, i) => `${i === 0 ? "M" : "L"}${xs[i].toFixed(1)},${y(p.score).toFixed(1)}`)
    .join(" ");

  // Notable changes: score delta >= 10 OR blacklist toggled OR dmarc policy changed
  const notable = new Set<number>();
  for (let i = 1; i < series.length; i++) {
    const a = series[i - 1];
    const b = series[i];
    if (Math.abs(b.score - a.score) >= 10) {
      notable.add(i);
      continue;
    }
    const aBl = a.blacklists ?? {};
    const bBl = b.blacklists ?? {};
    const keys = new Set([...Object.keys(aBl), ...Object.keys(bBl)]);
    for (const k of keys)
      if (!!aBl[k] !== !!bBl[k]) {
        notable.add(i);
        break;
      }
    if ((a.dmarc_policy ?? null) !== (b.dmarc_policy ?? null)) notable.add(i);
  }
  notable.add(series.length - 1); // always mark most recent

  const first = series[0].score;
  const last = series[series.length - 1].score;
  const delta = last - first;
  const trendColor =
    delta > 0 ? "text-success" : delta < 0 ? "text-destructive" : "text-muted-foreground";

  return (
    <div className="mt-4 pt-3 border-t border-border">
      <div className="flex items-center justify-between text-[11px] mb-1.5">
        <span className="uppercase tracking-wider text-muted-foreground">
          Score-Verlauf · letzte {series.length}
        </span>
        <span className={trendColor}>
          {delta > 0 ? "▲" : delta < 0 ? "▼" : "•"} {delta > 0 ? "+" : ""}
          {delta} ggü. Start
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14 overflow-visible">
        <line
          x1={PAD}
          y1={y(70)}
          x2={W - PAD}
          y2={y(70)}
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="2 3"
          className="text-muted-foreground/30"
        />
        <path
          d={path}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={currentScore >= 70 ? "text-success" : "text-warning"}
        />
        {series.map((p, i) => {
          const isNotable = notable.has(i);
          return (
            <g key={i}>
              <circle
                cx={xs[i]}
                cy={y(p.score)}
                r={isNotable ? 3 : 1.8}
                className={
                  isNotable
                    ? p.score >= 70
                      ? "fill-success"
                      : "fill-warning"
                    : "fill-muted-foreground"
                }
              >
                <title>{`${new Date(p.checked_at).toLocaleString()} — Score ${p.score}${isNotable && i > 0 ? ` (Δ ${p.score - series[i - 1].score >= 0 ? "+" : ""}${p.score - series[i - 1].score})` : ""}`}</title>
              </circle>
            </g>
          );
        })}
      </svg>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
        <span>{new Date(series[0].checked_at).toLocaleDateString()}</span>
        <span>{new Date(series[series.length - 1].checked_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

// Live-Status-Pille pro Domain während eines Bulk-Runs.
function RunPill({
  entry,
}: {
  entry: {
    state: "waiting" | "running" | "ok" | "failed";
    cached?: boolean;
    score?: number;
    listed?: number;
    total?: number;
    started_at?: number;
    finished_at?: number;
  };
}) {
  const duration =
    entry.started_at && entry.finished_at
      ? `${((entry.finished_at - entry.started_at) / 1000).toFixed(1)}s`
      : null;
  if (entry.state === "waiting") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
        <Clock className="size-3" /> wartet
      </span>
    );
  }
  if (entry.state === "running") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-medium">
        <Loader2 className="size-3 animate-spin" /> läuft…
      </span>
    );
  }
  if (entry.state === "ok") {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-success/15 text-success text-[10px] font-medium">
        <Check className="size-3" />
        {entry.cached ? "cache" : "ok"}
        {entry.score != null && ` · ${entry.score}/100`}
        {entry.listed != null && entry.total != null && ` · ${entry.listed}/${entry.total}`}
        {duration && ` · ${duration}`}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-destructive/15 text-destructive text-[10px] font-medium">
      <X className="size-3" /> fehlgeschlagen
      {duration && ` · ${duration}`}
    </span>
  );
}
function QuorumSettingsDialog({
  open,
  onOpenChange,
  value,
  onSave,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  value: QuorumConfig;
  onSave: (v: QuorumConfig) => void;
}) {
  const [defVal, setDefVal] = useState<number>(value.default);
  const [overrides, setOverrides] = useState<Record<string, number>>(value.perZone ?? {});

  // Reset state when dialog opens
  const openRef = useRef(open);
  if (open && !openRef.current) {
    setDefVal(value.default);
    setOverrides(value.perZone ?? {});
  }
  openRef.current = open;

  const setZone = (zone: string, v: number | null) => {
    setOverrides((s) => {
      const next = { ...s };
      if (v == null) delete next[zone];
      else next[zone] = Math.min(MAX_QUORUM, Math.max(1, Math.round(v)));
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Quorum-Einstellungen pro DNSBL</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground text-xs leading-relaxed">
            Ein Listing wird nur dann als bestätigt gewertet, wenn mindestens{" "}
            <b>N von {MAX_QUORUM}</b> Resolvern (Cloudflare, Google, Quad9) dasselbe Ergebnis
            liefern. Strenger (3/3) = weniger False-Positives, aber häufiger UNCONFIRMED. Lockerer
            (1/3) = jeder einzelne Hit zählt sofort als Listing.
          </p>

          <div className="rounded-md border border-border p-3 bg-muted/20">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Standard für alle DNSBLs
            </Label>
            <div className="flex items-center gap-3 mt-2">
              <input
                type="range"
                min={1}
                max={MAX_QUORUM}
                step={1}
                value={defVal}
                onChange={(e) => setDefVal(Number(e.target.value))}
                className="flex-1 accent-primary"
              />
              <span className="font-mono text-base w-12 text-right">
                {defVal}/{MAX_QUORUM}
              </span>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Pro DNSBL überschreiben (optional)
            </Label>
            <div className="max-h-72 overflow-y-auto border border-border rounded-md divide-y divide-border/40">
              {ALL_ZONES.map((zone) => {
                const isIp = (IP_DNSBL_ZONES as readonly string[]).includes(zone);
                const current = overrides[zone];
                return (
                  <div
                    key={zone}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-mono truncate">{zone}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {isIp ? "IP-Liste" : "Domain-Liste"} · effektiv:{" "}
                        <b>
                          {current ?? defVal}/{MAX_QUORUM}
                        </b>
                        {current != null ? " (override)" : " (Standard)"}
                      </div>
                    </div>
                    <select
                      className="h-7 text-xs border border-border rounded bg-background px-1"
                      value={current ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setZone(zone, v === "" ? null : Number(v));
                      }}
                    >
                      <option value="">Standard</option>
                      {Array.from({ length: MAX_QUORUM }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>
                          {n}/{MAX_QUORUM}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDefVal(2);
                setOverrides({});
              }}
            >
              Zurücksetzen
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  onSave({ default: defVal, perZone: overrides });
                  onOpenChange(false);
                  toast.success(
                    `Quorum gespeichert: Standard ${defVal}/${MAX_QUORUM}` +
                      (Object.keys(overrides).length
                        ? ` · ${Object.keys(overrides).length} Overrides`
                        : ""),
                  );
                }}
              >
                Speichern
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type DomainRow = {
  id: string;
  domain: string;
  reputation_score: number;
  blacklist_status: any;
};

type RunEntryLite = {
  state: "waiting" | "running" | "ok" | "failed";
  cached?: boolean;
};

function DomainListView({
  rows,
  runStatus,
  busyId,
  onRefresh,
  onDelete,
  onOpenDetail,
}: {
  rows: DomainRow[];
  runStatus: Record<string, RunEntryLite>;
  busyId?: string;
  onRefresh: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  onOpenDetail: (id: string) => void;
}) {
  return (
    <Card className="bg-card/60 border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Domain</th>
              <th className="text-right px-3 py-2 font-medium">Score</th>
              <th className="text-left px-3 py-2 font-medium">Blacklist</th>
              <th className="text-left px-3 py-2 font-medium">DMARC</th>
              <th className="text-left px-3 py-2 font-medium">SPF/MX</th>
              <th className="text-right px-3 py-2 font-medium">Alter</th>
              <th className="text-left px-3 py-2 font-medium">Registrar</th>
              <th className="text-left px-3 py-2 font-medium">Geprüft</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const s = (d.blacklist_status ?? {}) as {
                checked_at?: string;
                mx?: boolean;
                spf?: boolean;
                dmarc?: boolean;
                dmarc_policy?: string | null;
                blacklist_summary?: { total: number; listed: number };
                whois?: { age_days: number | null; registrar: string | null };
              };
              const score = d.reputation_score ?? 0;
              const scoreCls =
                score >= 80 ? "text-success" : score >= 60 ? "text-warning" : "text-destructive";
              const bl = s.blacklist_summary;
              const blCls = !bl
                ? "text-muted-foreground"
                : bl.listed === 0
                  ? "text-success"
                  : "text-destructive";
              const age = s.whois?.age_days;
              const ageLabel =
                typeof age === "number"
                  ? age < 30
                    ? `${age}d (jung)`
                    : age < 365
                      ? `${age}d`
                      : `${Math.floor(age / 365)}J ${age % 365}d`
                  : "—";
              const ageCls =
                typeof age === "number" && age < 30 ? "text-warning" : "text-foreground";
              const run = runStatus[d.id];
              const busy = busyId === d.id;
              return (
                <tr key={d.id} className="border-t border-border hover:bg-muted/20">
                  <td className="px-4 py-2 font-mono font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onOpenDetail(d.id)}
                        className="text-left hover:underline underline-offset-2 hover:text-primary transition"
                        title="Vollständige Details anzeigen"
                      >
                        {d.domain}
                      </button>
                      {run && <RunPill entry={run as any} />}
                    </div>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${scoreCls}`}>
                    {score}
                    <span className="text-muted-foreground font-normal">/100</span>
                  </td>
                  <td className={`px-3 py-2 ${blCls}`}>
                    {bl
                      ? bl.listed === 0
                        ? `✓ sauber (0/${bl.total})`
                        : `🚨 ${bl.listed}/${bl.total}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {s.dmarc ? (
                      <span
                        className={
                          s.dmarc_policy === "reject"
                            ? "text-success"
                            : s.dmarc_policy === "quarantine"
                              ? "text-warning"
                              : "text-muted-foreground"
                        }
                      >
                        {s.dmarc_policy ?? "ok"}
                      </span>
                    ) : (
                      <span className="text-destructive">fehlt</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={s.spf ? "text-success" : "text-destructive"}>SPF</span>
                    <span className="text-muted-foreground"> · </span>
                    <span className={s.mx ? "text-success" : "text-destructive"}>MX</span>
                  </td>
                  <td className={`px-3 py-2 text-right font-mono ${ageCls}`}>{ageLabel}</td>
                  <td className="px-3 py-2 text-muted-foreground truncate max-w-[160px]">
                    {s.whois?.registrar ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {s.checked_at ? new Date(s.checked_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-7"
                        onClick={() => onRefresh(d.id)}
                        disabled={busy}
                        title="Reputation jetzt frisch prüfen"
                      >
                        <RefreshCw className={`size-3.5 ${busy ? "animate-spin" : ""}`} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-7"
                        onClick={() => onDelete(d.id, d.domain)}
                        title="Domain löschen"
                      >
                        <Trash2 className="size-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function DomainDetailDrawer({
  domain,
  onOpenChange,
  onRefresh,
  busy,
}: {
  domain: { id: string; domain: string; reputation_score: number; blacklist_status: any } | null;
  onOpenChange: (open: boolean) => void;
  onRefresh: (id: string) => void;
  busy: boolean;
}) {
  const open = !!domain;
  const status = (domain?.blacklist_status ?? {}) as any;
  const checked = !!status.checked_at;
  const score = domain?.reputation_score ?? 0;
  const ok = score >= 70;
  const scoreCls = score >= 80 ? "text-success" : score >= 60 ? "text-warning" : "text-destructive";
  const dnsblEntries: any[] = status.audit?.dnsbl ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto bg-card border-l border-border"
      >
        {!domain ? null : (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <span className="font-mono">{domain.domain}</span>
                {ok ? (
                  <ShieldCheck className="size-5 text-success" />
                ) : (
                  <ShieldAlert className="size-5 text-warning" />
                )}
              </SheetTitle>
              <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span>
                  {checked
                    ? `Geprüft ${new Date(status.checked_at).toLocaleString()}`
                    : "Noch nicht geprüft"}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRefresh(domain.id)}
                  disabled={busy}
                >
                  <RefreshCw className={`size-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />
                  Frisch prüfen
                </Button>
              </div>
            </SheetHeader>

            <div className="mt-5 space-y-4 text-xs">
              {/* Score */}
              <section>
                <div className="flex items-baseline justify-between">
                  <span className="uppercase tracking-wider text-[10px] text-muted-foreground">
                    Reputation Score
                  </span>
                  <span className={`text-3xl font-semibold ${scoreCls}`}>
                    {score}
                    <span className="text-xs text-muted-foreground font-normal">/100</span>
                  </span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full ${ok ? "bg-success" : "bg-warning"}`}
                    style={{ width: `${score}%` }}
                  />
                </div>
              </section>

              {/* Score-Aufschlüsselung */}
              {status.breakdown && status.breakdown.length > 0 && (
                <section className="rounded-md border border-border bg-muted/20 p-3">
                  <h3 className="uppercase tracking-wider text-[10px] text-muted-foreground mb-2">
                    Score-Aufschlüsselung
                  </h3>
                  <ul className="space-y-1">
                    {status.breakdown.map((b: any, i: number) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="text-muted-foreground">{b.label}</span>
                        <span
                          className={`font-mono font-semibold ${
                            b.delta > 0
                              ? "text-success"
                              : b.delta < 0
                                ? "text-destructive"
                                : "text-muted-foreground"
                          }`}
                        >
                          {b.delta > 0 ? "+" : ""}
                          {b.delta}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {checked && (
                <section className="space-y-2">
                  <h3 className="uppercase tracking-wider text-[10px] text-muted-foreground">
                    Authentifizierung
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    <CheckBadge label="MX" ok={!!status.mx} />
                    <CheckBadge label="SPF" ok={!!status.spf} />
                    <CheckBadge
                      label={`DMARC${status.dmarc_policy ? ` (${status.dmarc_policy})` : ""}`}
                      ok={!!status.dmarc}
                    />
                    <CheckBadge label="MTA-STS" ok={!!status.mta_sts?.found} />
                    <CheckBadge label="TLS-RPT" ok={!!status.tls_rpt?.found} />
                    <CheckBadge label="BIMI" ok={!!status.bimi?.found} />
                  </div>
                </section>
              )}

              {/* DNS-Records */}
              {checked &&
                (status.spf_record ||
                  status.dmarc_record ||
                  status.mx_hosts?.length ||
                  status.a_records?.length) && (
                  <section className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
                    <h3 className="uppercase tracking-wider text-[10px] text-muted-foreground">
                      DNS-Records
                    </h3>
                    <div className="space-y-2 text-[11px]">
                      {status.mx_hosts?.length > 0 && (
                        <div>
                          <div className="text-muted-foreground">MX</div>
                          {status.mx_hosts.map((m: any, i: number) => (
                            <div key={i} className="font-mono">
                              {m.priority} {m.host}
                            </div>
                          ))}
                        </div>
                      )}
                      {status.a_records?.length > 0 && (
                        <div>
                          <div className="text-muted-foreground">A / PTR</div>
                          {status.a_records.map((a: string, i: number) => {
                            const ptr = status.ptr_records?.find((p: any) => p.ip === a)?.ptr;
                            return (
                              <div key={i} className="font-mono">
                                {a}
                                {ptr ? (
                                  <span className="text-muted-foreground"> → {ptr}</span>
                                ) : (
                                  <span className="text-destructive"> (kein PTR)</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {status.ns_records?.length > 0 && (
                        <div>
                          <div className="text-muted-foreground">NS</div>
                          {status.ns_records.map((n: string, i: number) => (
                            <div key={i} className="font-mono">
                              {n}
                            </div>
                          ))}
                        </div>
                      )}
                      {status.spf_record && (
                        <div>
                          <div className="text-muted-foreground">SPF</div>
                          <div className="font-mono break-all">{status.spf_record}</div>
                          {status.spf_analysis && (
                            <div className="text-muted-foreground">
                              {status.spf_analysis.lookups_estimate} DNS-Lookups · all=
                              {status.spf_analysis.all_mechanism ?? "—"}
                              {status.spf_analysis.includes.length > 0 &&
                                ` · ${status.spf_analysis.includes.length} includes`}
                            </div>
                          )}
                        </div>
                      )}
                      {status.dmarc_record && (
                        <div>
                          <div className="text-muted-foreground">DMARC</div>
                          <div className="font-mono break-all">{status.dmarc_record}</div>
                          {status.dmarc_analysis && (
                            <div className="text-muted-foreground">
                              pct={status.dmarc_analysis.pct ?? "100"} · sp=
                              {status.dmarc_analysis.sp ?? "—"} · adkim=
                              {status.dmarc_analysis.adkim ?? "r"} · aspf=
                              {status.dmarc_analysis.aspf ?? "r"}
                            </div>
                          )}
                        </div>
                      )}
                      {status.dkim?.some((k: any) => k.found) && (
                        <div>
                          <div className="text-muted-foreground">DKIM</div>
                          {status.dkim
                            .filter((k: any) => k.found)
                            .map((k: any, i: number) => (
                              <div key={i} className="font-mono break-all">
                                <span className="text-primary">{k.selector}</span>:{" "}
                                {k.record?.slice(0, 120)}…
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </section>
                )}

              {/* Blacklists */}
              {status.blacklist_summary && (
                <section className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="uppercase tracking-wider text-[10px] text-muted-foreground">
                      Blacklists
                    </h3>
                    <span
                      className={`font-semibold text-[11px] ${
                        status.blacklist_summary.listed === 0 ? "text-success" : "text-destructive"
                      }`}
                    >
                      {status.blacklist_summary.listed === 0
                        ? `✓ sauber (0/${status.blacklist_summary.total})`
                        : `🚨 ${status.blacklist_summary.listed}/${status.blacklist_summary.total} gelistet`}
                    </span>
                  </div>
                  {status.blacklists && Object.keys(status.blacklists).length > 0 && (
                    <div className="grid grid-cols-2 gap-1">
                      {Object.entries(status.blacklists).map(([z, listed]) => (
                        <CheckBadge
                          key={z}
                          label={z}
                          ok={!listed}
                          okText="clean"
                          badText="LISTED"
                        />
                      ))}
                    </div>
                  )}
                  {dnsblEntries.length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground">
                        DNSBL Audit · {dnsblEntries.length} Lookups
                      </summary>
                      <div className="mt-2">
                        <DnsblAuditTable entries={dnsblEntries as any} />
                      </div>
                    </details>
                  )}
                </section>
              )}

              {/* WHOIS / RDAP */}
              {status.whois && (
                <section className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="uppercase tracking-wider text-[10px] text-muted-foreground">
                      WHOIS / RDAP
                    </h3>
                    {status.whois.source && (
                      <span className="text-[10px] text-muted-foreground">
                        {status.whois.source}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                    <div className="text-muted-foreground">Registrar</div>
                    <div
                      className="truncate text-right font-medium"
                      title={status.whois.registrar ?? undefined}
                    >
                      {status.whois.registrar ?? "—"}
                    </div>
                    <div className="text-muted-foreground">Alter</div>
                    <div className="text-right font-medium">
                      {status.whois.age_days != null ? `${status.whois.age_days} Tage` : "—"}
                    </div>
                    <div className="text-muted-foreground">Registriert</div>
                    <div className="text-right">
                      {status.whois.registered_at
                        ? new Date(status.whois.registered_at).toLocaleDateString()
                        : "—"}
                    </div>
                    <div className="text-muted-foreground">Läuft ab</div>
                    <div className="text-right">
                      {status.whois.expires_at
                        ? new Date(status.whois.expires_at).toLocaleDateString()
                        : "—"}
                    </div>
                    {status.whois.status?.length > 0 && (
                      <>
                        <div className="text-muted-foreground">Status</div>
                        <div className="text-right font-mono text-[10px] break-all">
                          {status.whois.status.join(", ")}
                        </div>
                      </>
                    )}
                  </div>
                  {status.whois.error && (
                    <div className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-[11px] text-destructive">
                      Abbruchgrund: {status.whois.error}
                    </div>
                  )}
                  {status.whois.attempts && status.whois.attempts.length > 0 && (
                    <details open>
                      <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground">
                        RDAP-Versuche ({status.whois.attempts.length})
                      </summary>
                      <ul className="mt-1 space-y-0.5 text-[11px] font-mono">
                        {status.whois.attempts.map((a: any, i: number) => (
                          <li
                            key={i}
                            className={
                              a.ok
                                ? "text-success"
                                : a.timed_out
                                  ? "text-warning"
                                  : "text-destructive"
                            }
                          >
                            {a.ok ? "✓" : a.timed_out ? "⏱" : "✗"} #{a.attempt} {a.host} —{" "}
                            {a.ok ? `HTTP ${a.http_status} OK` : (a.error ?? "Fehler")}{" "}
                            <span className="text-muted-foreground">
                              ({a.duration_ms}ms
                              {a.retried_after_ms != null
                                ? `, Retry in ${a.retried_after_ms}ms`
                                : ""}
                              )
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </section>
              )}

              {/* Empfehlungen */}
              {status.recommendations && status.recommendations.length > 0 && (
                <section className="rounded-md border border-primary/40 bg-primary/5 p-3 space-y-2">
                  <h3 className="uppercase tracking-wider text-[10px] text-primary font-semibold">
                    🛠 {status.recommendations.length} Empfehlung
                    {status.recommendations.length === 1 ? "" : "en"}
                  </h3>
                  <div className="space-y-2">
                    {status.recommendations.map((r: any, i: number) => (
                      <div key={i} className="rounded border border-border bg-background/40 p-2">
                        <div className="flex items-center gap-2">
                          <SeverityBadge sev={r.severity} />
                          <span className="font-semibold">{r.title}</span>
                        </div>
                        <div className="mt-1 text-muted-foreground">{r.why}</div>
                        <div className="mt-1">
                          <span className="text-foreground font-medium">Fix:</span> {r.how}
                        </div>
                        {r.example && (
                          <pre className="mt-1 overflow-x-auto rounded bg-muted/40 p-1.5 font-mono text-[10px] leading-tight">
                            {r.example}
                          </pre>
                        )}
                        {r.link && (
                          <a
                            href={r.link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 inline-block text-primary underline underline-offset-2"
                          >
                            {r.link.label} ↗
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Notes */}
              {status.notes?.length > 0 && (
                <section className="rounded-md border border-border bg-muted/20 p-3">
                  <h3 className="uppercase tracking-wider text-[10px] text-muted-foreground mb-1">
                    Notizen
                  </h3>
                  <ul className="text-muted-foreground space-y-0.5">
                    {status.notes.map((n: string, i: number) => (
                      <li key={i}>• {n}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
